import React, { useEffect, useMemo, useState } from "react";
import { toLocalYmd } from "../../../utils/dateTime";
import { useNavigate } from "react-router-dom";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import CustomBtn from "../../../components/Buttons/CustomBtn";
import SearchableTable from "../../../components/Tables/SearchableTable";
import { LoanColumns } from "../../../assets/Columns/LoanColumns";
import { fetchLoanLog, fetchLoans } from "../../../store/Slices/LoanSlice";
import { fetchAccounts } from "../../../store/Slices/AccountSlice";
import { useDispatch, useSelector } from "react-redux";
import AddLoanModal from "../../../components/Modals/AddLoanModal";
import EditLoanModal from "../../../components/Modals/EditLoanModal";
import EditLoanDisbursementModal from "../../../components/Modals/EditLoanDisbursementModal";
import EditLoanPaymentModal from "../../../components/Modals/EditLoanPaymentModal";
import DeleteModal from "../../../components/Modals/DeleteModal";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import CustomTabs from "../../../components/Tabs/CustomTabs";
import LoanTransactionLogCards from "../../../components/Loan/LoanTransactionLogCards";
import LoanPartyCard from "../../../components/Loan/LoanPartyCard";
import AuthInput from "../../../components/Input/AuthInput";
import { BsSearch } from "react-icons/bs";
import { filterTableRowsBySearch } from "../../../utils/tableSearch";
import { getAppBasePath } from "../../../utils/appPaths";
import { DeleteLoanApi, DeleteLoanPaymentApi } from "../../../ApiRequests";
import { ErrorToast, SuccessToast } from "../../../utils/ShowToast";
import { isReadOnlyLoanViewer } from "../../../utils/auth";
import {
  computeLoanRunningBalances,
  getLoanBalance,
} from "../../../utils/LoanConverter";

const LOAN_TABS = [{ label: "Party accounts" }, { label: "Loan log" }];

const Loan = () => {
  const navigate = useNavigate();
  const base = getAppBasePath();
  const [searchText, setSearchText] = useState("");
  const [logSearch, setLogSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selected, setSelected] = useState("");
  const [editDisbursement, setEditDisbursement] = useState(null);
  const [editRepayment, setEditRepayment] = useState(null);
  const [deleteDisbursement, setDeleteDisbursement] = useState(null);
  const [deletePayment, setDeletePayment] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const readOnly = isReadOnlyLoanViewer();

  const dispatch = useDispatch();
  const Loans = useSelector((state) => state.Loans);

  useEffect(() => {
    dispatch(fetchLoans());
    dispatch(fetchLoanLog());
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === 1) dispatch(fetchLoanLog());
  }, [activeTab, dispatch]);

  const filteredByDate = useMemo(() => {
    return (Loans.data || [])
      .map((row) => ({
        ...row,
        balance: getLoanBalance(row),
      }))
      .filter((dt) => {
        const loanDate = dt.date
          ? toLocalYmd(dt.date)
          : "";
        const matchFrom = !fromDate || !loanDate || new Date(loanDate) >= new Date(fromDate);
        const matchTo = !toDate || !loanDate || new Date(loanDate) <= new Date(toDate);
        return matchFrom && matchTo;
      });
  }, [Loans.data, fromDate, toDate]);

  const filteredLog = useMemo(() => {
    const q = logSearch.trim().toLowerCase();
    return (Loans.log || []).filter((row) => {
      const rowDate = row.date
        ? toLocalYmd(row.date)
        : "";
      const matchFrom = !fromDate || !rowDate || new Date(rowDate) >= new Date(fromDate);
      const matchTo = !toDate || !rowDate || new Date(rowDate) <= new Date(toDate);
      if (!matchFrom || !matchTo) return false;
      if (!q) return true;
      return (
        String(row.partyName || "").toLowerCase().includes(q) ||
        String(row.desc || "").toLowerCase().includes(q) ||
        String(row.marka || "").toLowerCase().includes(q) ||
        String(row.bichak_no || "").toLowerCase().includes(q) ||
        String(row.accountName || "").toLowerCase().includes(q)
      );
    });
  }, [Loans.log, fromDate, toDate, logSearch]);

  const filteredLogWithBalance = useMemo(() => {
    const balanceMap = computeLoanRunningBalances(Loans.log || [], {
      perAccount: true,
    });
    return filteredLog.map((row) => ({
      ...row,
      balanceAfter: balanceMap.get(row._id) ?? 0,
    }));
  }, [Loans.log, filteredLog]);

  const filteredParties = useMemo(() => {
    return filterTableRowsBySearch(filteredByDate, searchText, LoanColumns);
  }, [filteredByDate, searchText]);

  const summary = useMemo(() => {
    let principal = 0;
    let balance = 0;
    let count = 0;
    (Loans.data || []).forEach((row) => {
      principal += Number(row.amount || 0);
      balance += getLoanBalance(row);
      count += 1;
    });
    return { principal, balance, count };
  }, [Loans.data]);

  return (
    <BodyWrapper>
      <Header
        title="Loans"
        desc="Loan given — party accounts and date-wise log"
      >
        <CustomBtn title="Give loan" onClick={() => setOpenModal(true)} />
      </Header>

      <div className="mb-4">
        <div className="rounded-2xl border border-danger/20 bg-gradient-to-br from-dangerSoft via-white to-white px-4 py-3 w-full max850:max-w-none max-w-md shadow-soft">
          <p className="text-xs font-bold uppercase text-danger">Loans given</p>
          <p className="text-lg max850:text-xl font-extrabold text-danger tabular-nums">
            Rs {summary.principal.toLocaleString("en-PK")}
          </p>
          <p className="text-xs text-danger/80">
            Outstanding Rs {summary.balance.toLocaleString("en-PK")} ·{" "}
            {summary.count} parties
          </p>
        </div>
      </div>

      <div className="mb-4">
        <CustomTabs
          tabs={LOAN_TABS}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          scrollable
        />
      </div>

      <div className="flex flex-col max850:gap-3 gap-4 mb-4">
        <div className="flex gap-3 max850:flex-col flex-wrap">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-sm font-medium mb-1 text-inkSecondary">From</label>
            <input
              type="date"
              className="border border-line bg-card text-ink px-3 py-2 rounded-lg w-full max850:py-2.5 outline-none focus:border-main focus:ring-2 focus:ring-main/15"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-sm font-medium mb-1 text-inkSecondary">To</label>
            <input
              type="date"
              className="border border-line bg-card text-ink px-3 py-2 rounded-lg w-full max850:py-2.5 outline-none focus:border-main focus:ring-2 focus:ring-main/15"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {activeTab === 0 && (
        <div className="w-full mt-2">
          {Loans.loading ? (
            <FetchingLoading />
          ) : (
            <>
              {/* Mobile — party account cards */}
              <div className="max850:block hidden space-y-4">
                <div className="relative">
                  <BsSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search party, phone…"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-line bg-card text-ink text-sm shadow-soft focus:outline-none focus:border-main focus:ring-2 focus:ring-main/15"
                  />
                </div>

                {filteredParties.length === 0 ? (
                  <div className="text-center py-12 rounded-2xl border border-dashed border-line text-muted text-sm">
                    No loan parties found for this period.
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-muted font-medium px-0.5">
                      {filteredParties.length} part
                      {filteredParties.length === 1 ? "y" : "ies"}
                    </p>
                    <div className="flex flex-col gap-3 pb-2">
                      {filteredParties.map((row) => (
                        <LoanPartyCard
                          key={row._id}
                          row={row}
                          onOpen={() => navigate(`${base}/loans/${row._id}`)}
                          onEdit={(party) => {
                            setSelected(party);
                            setOpenEditModal(true);
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Desktop — table */}
              <div className="max850:hidden">
                <SearchableTable
                  setOpenEditModal={setOpenEditModal}
                  setSelected={setSelected}
                  enableLoanDetail
                  SearchPlaceholder="Search party, phone…"
                  SearchText={searchText}
                  setSearchText={setSearchText}
                  CurrentData={filteredByDate}
                  Columns={LoanColumns}
                />
              </div>
            </>
          )}
          <p className="text-xs text-muted mt-3 px-1 max850:hidden">
            Eye icon = party account · full date-wise log on{" "}
            <button
              type="button"
              className="font-semibold text-main hover:underline"
              onClick={() => setActiveTab(1)}
            >
              Loan log
            </button>{" "}
            tab
          </p>
        </div>
      )}

      {activeTab === 1 && (
        <div className="w-full mt-2 space-y-4">
          <AuthInput
            Value={logSearch}
            setValue={setLogSearch}
            placeholder="Search party, marka, bichak no, description…"
          />
          {Loans.logLoading ? (
            <FetchingLoading />
          ) : (
            <LoanTransactionLogCards
              entries={filteredLogWithBalance}
              emptyMessage="No loan entries in this period — give a loan to see date-wise history here."
              onEditDisbursement={readOnly ? undefined : setEditDisbursement}
              onEditRepayment={readOnly ? undefined : setEditRepayment}
              onDeleteDisbursement={readOnly ? undefined : setDeleteDisbursement}
              onDeleteRepayment={readOnly ? undefined : setDeletePayment}
            />
          )}
        </div>
      )}

      {openModal && <AddLoanModal Open={openModal} setOpen={setOpenModal} />}
      {openEditModal && (
        <EditLoanModal
          open={openEditModal}
          setOpen={setOpenEditModal}
          selectedLoan={selected}
        />
      )}
      {editDisbursement && (
        <EditLoanDisbursementModal
          open={!!editDisbursement}
          setOpen={() => setEditDisbursement(null)}
          entry={editDisbursement}
          onSuccess={() => {
            dispatch(fetchLoans());
            dispatch(fetchLoanLog());
            dispatch(fetchAccounts());
          }}
        />
      )}
      {editRepayment && (
        <EditLoanPaymentModal
          open={!!editRepayment}
          setOpen={() => setEditRepayment(null)}
          entry={editRepayment}
          onSuccess={() => {
            dispatch(fetchLoans());
            dispatch(fetchLoanLog());
            dispatch(fetchAccounts());
          }}
        />
      )}
      {deletePayment && (
        <DeleteModal
          Open={!!deletePayment}
          setOpen={() => setDeletePayment(null)}
          Loading={deleting}
          onSubmit={async () => {
            setDeleting(true);
            try {
              const res = await DeleteLoanPaymentApi(deletePayment._id);
              if (res.data.success) {
                SuccessToast("Repayment removed");
                setDeletePayment(null);
                dispatch(fetchLoans());
                dispatch(fetchLoanLog());
                dispatch(fetchAccounts());
              } else ErrorToast(res.data.error?.msg);
            } catch (e) {
              ErrorToast(e.response?.data?.error?.msg || "Delete failed");
            }
            setDeleting(false);
          }}
        />
      )}
      {deleteDisbursement && (
        <DeleteModal
          Open={!!deleteDisbursement}
          setOpen={() => setDeleteDisbursement(null)}
          Loading={deleting}
          onSubmit={async () => {
            setDeleting(true);
            try {
              const res = await DeleteLoanApi(deleteDisbursement._id);
              if (res.data.success) {
                SuccessToast("Entry removed");
                setDeleteDisbursement(null);
                dispatch(fetchLoans());
                dispatch(fetchLoanLog());
                dispatch(fetchAccounts());
              } else ErrorToast(res.data.error?.msg);
            } catch (e) {
              ErrorToast(e.response?.data?.error?.msg || "Delete failed");
            }
            setDeleting(false);
          }}
        />
      )}
    </BodyWrapper>
  );
};

export default Loan;

